import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import useTranslation from 'hooks/use-translation';

import { palette } from './palette';
import { Container, RevealOnScroll, Section, SectionHead } from './primitives';
import { useReveal } from './use-reveal';

const COUNT_UP_DURATION_MS = 1400;

function useCountUp(target: string, start: boolean): string {
  const [display, setDisplay] = useState(target);

  useEffect(() => {
    const match = /^([^\d]*)([\d,.]+)(.*)$/.exec(target);

    if (!match) {
      setDisplay(target);
      return undefined;
    }

    const [, prefix, numberText, suffix] = match;
    const total = parseFloat(numberText.replace(/,/g, ``));

    if (!start) {
      setDisplay(`${prefix}0${suffix}`);
      return undefined;
    }

    if (typeof window === `undefined` || window.matchMedia(`(prefers-reduced-motion: reduce)`).matches) {
      setDisplay(target);
      return undefined;
    }

    let frame = 0;
    const startTime = performance.now();

    const step = (now: number): void => {
      const progress = Math.min(1, (now - startTime) / COUNT_UP_DURATION_MS);
      const eased = 1 - (1 - progress) ** 3;
      const current = eased * total;
      const formatted = total >= 1000 ? Math.round(current).toLocaleString() : Math.round(current).toString();
      setDisplay(`${prefix}${formatted}${suffix}`);

      if (progress < 1) {
        frame = requestAnimationFrame(step);
      }
    };

    frame = requestAnimationFrame(step);
    return () => cancelAnimationFrame(frame);
  }, [target, start]);

  return display;
}

type StatItemProps = {
  value: string;
  label: string;
  start: boolean;
};

function StatItem({ value, label, start }: StatItemProps): JSX.Element {
  const display = useCountUp(value, start);

  return (
    <Stat>
      <div className='n'>{display}</div>
      <p>{label}</p>
    </Stat>
  );
}

export function ReviewsStatsSection(): JSX.Element {
  const { t } = useTranslation();
  const { ref, inView } = useReveal<HTMLDivElement>(0.4);

  const stats = [
    { value: `6500+`, label: t('home.redesign.statDispensaries', 'dispensaries trust dutchie') },
    { value: `40+`, label: t('home.redesign.statMarkets', '40+ markets across North America') },
    { value: `$100B+`, label: t('home.redesign.statDispensarySales', 'in dispensary sales, powered by dutchie') },
    { value: `1M+`, label: t('home.redesign.statDailyOrders', 'daily orders from dispensaries') },
  ];

  return (
    <Section $alt>
      <Container>
        <SectionHead
          first={t('home.redesign.reviewsFirst', 'Reviews')}
          rest={t('home.redesign.reviewsRest', 'from customers')}
          sub={t('home.redesign.reviewsSub', 'Find dispensaries with the best selection.')}
        />
        <RevealOnScroll>
          <Reviews>
            <Review>
              <Quote>"</Quote>
              <p>
                {t(
                  'home.redesign.reviewCustomer',
                  `"I found my new favorite dispensary through dutchie. Now I order from Green Room every week. Their menu is always up to date and my order is ready in 15 minutes."`
                )}
              </p>
              <Who>
                <strong>Sarah K.</strong>
                <span>Denver, CO</span>
              </Who>
            </Review>
            <Review>
              <Quote>"</Quote>
              <p>
                {t(
                  'home.redesign.reviewBusiness',
                  `"dutchie sends us customers we'd never have reached on our own. Our online orders tripled, and every customer is ours to keep, not dutchie's."`
                )}
              </p>
              <Who>
                <strong>{t('home.redesign.reviewBusinessWho', 'Operations Director')}</strong>
                <span>{t('home.redesign.reviewBusinessWhere', '12-location MSO')}</span>
              </Who>
            </Review>
          </Reviews>
        </RevealOnScroll>
        <Stats ref={ref}>
          {stats.map((stat) => (
            <StatItem key={stat.value} value={stat.value} label={stat.label} start={inView} />
          ))}
        </Stats>
      </Container>
    </Section>
  );
}

const Reviews = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    grid-template-columns: 1fr;
  }
`;

const Review = styled.div`
  background: ${palette.bgCard};
  border-radius: 16px;
  padding: 32px;

  p {
    margin: 0 0 22px;
    font-size: 16px;
    line-height: 1.55;
    color: ${palette.cream};
  }
`;

const Quote = styled.div`
  color: ${palette.gold};
  font-size: 40px;
  font-weight: 700;
  line-height: 1;
  margin-bottom: 14px;
  font-family: Georgia, serif;
`;

const Who = styled.div`
  strong {
    display: block;
    color: ${palette.cream};
  }

  span {
    color: ${palette.creamDim};
    font-size: 14px;
  }
`;

const Stats = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 24px;
  text-align: center;
  margin-top: 60px;

  ${({ theme }) => theme.breakpoints.down(`xs`)} {
    grid-template-columns: 1fr 1fr;
  }
`;

const Stat = styled.div`
  .n {
    font-size: 56px;
    color: ${palette.gold};
    font-weight: 700;
    line-height: 1;

    ${({ theme }) => theme.breakpoints.down(`xs`)} {
      font-size: 40px;
    }
  }

  p {
    margin: 10px auto 0;
    color: ${palette.creamDim};
    font-size: 14px;
    max-width: 220px;
  }
`;
